import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ShieldCheck } from "lucide-react";
import { api, img, rm, errMsg, useSeo } from "../../lib/api";
import { useAuth, useCart, useLang } from "../../lib/ctx";
import { ConditionBadge } from "../../components/store/ProductCard";

const Gallery = ({ images, title }) => {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-[4/5] overflow-hidden bg-[#141414]"><img src={img(images?.[i]?.url)} alt={images?.[i]?.alt || title} className="h-full w-full object-cover" data-testid="product-main-image" /></div>
      {images?.length > 1 && <div className="mt-3 flex gap-2">{images.map((m, k) => <button key={k} onClick={() => setI(k)} className={`h-20 w-16 border ${k === i ? "border-[#C8A45D]" : "border-transparent"}`}><img src={img(m.url)} alt={m.alt} className="h-full w-full object-cover" /></button>)}</div>}
    </div>
  );
};

export default function Product() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const { add } = useCart();
  const { user, setUser } = useAuth();
  const [p, setP] = useState(null);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setColor(r.data.variants[0]?.color || "");
      api.get(`/products/${r.data.id}/reviews`).then((x) => setReviews(x.data));
    }).catch(() => setP(false));
  }, [slug]);
  const colors = useMemo(() => [...new Set((p?.variants || []).map((v) => v.color))], [p]);
  const sizes = (p?.variants || []).filter((v) => v.color === color);
  const variant = sizes.find((v) => v.size === size) || (sizes.length === 1 ? sizes[0] : null);
  useSeo(p ? p.seo?.title || p.title : "Product", p?.seo?.description || p?.description?.slice(0, 155), p ? {
    "@context": "https://schema.org", "@type": "Product", name: p.title, brand: { "@type": "Brand", name: p.brand }, image: (p.images || []).map((m) => img(m.url)), description: p.description,
    sku: p.variants[0]?.sku, itemCondition: p.condition === "vintage_preowned" ? "https://schema.org/UsedCondition" : "https://schema.org/NewCondition",
    offers: { "@type": "AggregateOffer", priceCurrency: "MYR", lowPrice: p.price_from, availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
  } : null);
  if (p === false) return <p className="p-20 text-center text-zinc-400">Product not found.</p>;
  if (!p) return <p className="p-20 text-center text-zinc-500">Loading…</p>;
  const addCart = () => {
    if (!variant) return toast.error(t("selectSize"));
    add({ variant_id: variant.id, product_id: p.id, slug: p.slug, sku: variant.sku, title: p.title, color: variant.color, size: variant.size, price: variant.discount_price || variant.price, image: p.images?.[0]?.url, qty: 1, max: variant.available });
  };
  const wish = async () => {
    if (!user) return toast.error("Login to use wishlist");
    try {
      const { data } = await api.post(`/account/wishlist/${p.id}`);
      setUser({ ...user, wishlist: data.wishlist });
      toast.success(data.wishlist.includes(p.id) ? "Added to wishlist" : "Removed from wishlist");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const price = variant ? variant.discount_price || variant.price : p.price_from;
  return (
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-8 lg:grid-cols-2 lg:px-12" data-testid="product-page">
      <Gallery images={p.images} title={p.title} />
      <div>
        <div className="flex items-center gap-2"><ConditionBadge c={p.condition} />{p.one_of_one && <span className="bg-white px-2 py-0.5 font-mono text-[10px] uppercase text-black">1 of 1</span>}</div>
        <p className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500">{p.brand} · {p.model}</p>
        <h1 className="mt-2 font-display text-5xl leading-none sm:text-6xl" data-testid="product-title">{pick(p, "title")}</h1>
        <div className="mt-5 flex items-baseline gap-3">
          <p className="text-3xl font-semibold text-[#C8A45D]" data-testid="product-price">{rm(price)}</p>
          {variant?.discount_price && <p className="text-zinc-500 line-through">{rm(variant.price)}</p>}
          {p.promo && <span className="rounded-sm bg-[#ee4d2d] px-2 py-0.5 text-xs font-bold text-white" data-testid="product-promo-badge">{p.promo.name} -{p.promo.pct}%</span>}
        </div>
        {colors.length > 1 && (
          <div className="mt-8"><p className="eyebrow mb-3">{t("selectColor")}</p>
            <div className="flex flex-wrap gap-2">{colors.map((c) => <button key={c} onClick={() => { setColor(c); setSize(""); }} className={`border px-4 py-2 text-sm ${c === color ? "border-[#C8A45D] text-[#C8A45D]" : "border-white/20"}`} data-testid={`color-${c}`}>{c}</button>)}</div>
          </div>
        )}
        <div className="mt-8"><p className="eyebrow mb-3">{t("size")}</p>
          <div className="flex flex-wrap gap-2" data-testid="size-options">
            {sizes.map((v) => (
              <button key={v.id} disabled={!v.in_stock} onClick={() => setSize(v.size)} data-testid={`size-${v.size}`}
                className={`min-w-14 border px-4 py-2 text-sm transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:line-through ${variant?.id === v.id ? "border-[#C8A45D] bg-[#C8A45D] text-black" : "border-white/20 hover:border-white"}`}>{v.size}</button>
            ))}
          </div>
          {variant && <p className="mt-3 font-mono text-xs text-zinc-400" data-testid="stock-indicator">{variant.low_stock ? t("onlyLeft", { n: variant.available }) : variant.in_stock ? t("inStock") : t("soldOut")} · SKU {variant.sku}</p>}
        </div>
        <div className="mt-8 flex gap-3">
          <button onClick={addCart} disabled={!p.in_stock || (variant && !variant.in_stock)} className="btn-gold flex-1" data-testid="add-to-cart-btn">{p.in_stock ? t("addToCart") : t("soldOut")}</button>
          <button onClick={wish} className={`btn-ghost px-4 ${user?.wishlist?.includes(p.id) ? "text-[#C8A45D]" : ""}`} data-testid="wishlist-toggle-btn" aria-label="wishlist"><Heart className="h-4 w-4" /></button>
        </div>
        <div className="mt-10 space-y-6 border-t border-white/10 pt-8 text-sm">
          <div><p className="eyebrow mb-2">{t("description")}</p><p className="whitespace-pre-line leading-relaxed text-zinc-300" data-testid="product-description">{pick(p, "description")}</p></div>
          {p.material && <p className="text-zinc-400">Material: {p.material}</p>}
          {p.authenticity_note && <div className="flex gap-3 border border-[#C8A45D]/30 p-4"><ShieldCheck className="h-5 w-5 shrink-0 text-[#C8A45D]" /><div><p className="eyebrow mb-1">{t("authenticity")}</p><p className="text-zinc-300">{p.authenticity_note}</p></div></div>}
          {p.warranty_months > 0 && <p className="text-zinc-300"><span className="eyebrow mr-2">{t("warranty")}</span>{p.warranty_months} months — {p.warranty_summary}</p>}
        </div>
        <div className="mt-10 border-t border-white/10 pt-8" data-testid="product-reviews">
          <p className="eyebrow mb-3">{t("reviews")}</p>
          {!reviews.length ? <p className="text-sm text-zinc-500">{t("noReviews")}</p> : reviews.map((r) => <p key={r.id} className="mb-3 text-sm text-zinc-300">{"★".repeat(r.rating)} “{r.text}” — {r.name}</p>)}
        </div>
      </div>
    </div>
  );
}
